import { createFileRoute, Link } from '@tanstack/react-router'
import { HelpLayout } from '@/components/help-center/help-layout'
import { HelpArticleContent } from '@/components/help-center/help-article'

export const Route = createFileRoute('/_public/app/help/windows-phone-link')({
  component: WindowsPhoneLinkPage,
  head: () => ({
    meta: [
      { title: 'Windows Phone Link Setup | MessageDrafts Help' },
      {
        name: 'description',
        content:
          'Pair your Android phone with Windows 10/11 to send drafts from your desktop using Phone Link.',
      },
    ],
  }),
})

function WindowsPhoneLinkPage() {
  return (
    <HelpLayout
      title="Send drafts with Windows Phone Link"
      description="Pair your Android phone with Windows so you can send drafts from your desktop."
    >
      <HelpArticleContent
        slug="windows-phone-link"
        title="Overview"
        description="Use Windows Phone Link to send drafts from your PC while keeping messages on your phone."
        tags={['windows', 'phone-link', 'android', 'setup']}
        devices={['windows', 'android']}
      >
        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Requirements</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>Windows 10 or 11 PC with the Phone Link app installed.</li>
            <li>Android phone with the Link to Windows app.</li>
            <li>Both devices on the same Wi-Fi network or Bluetooth enabled.</li>
            <li>Microsoft account for pairing.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Step-by-step</h3>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Open Phone Link on Windows and choose Android.</li>
            <li>Install Link to Windows on your phone if prompted.</li>
            <li>Sign in with the same Microsoft account on both devices.</li>
            <li>Follow the pairing instructions and grant permissions.</li>
            <li>
              Once paired, open MessageDrafts in your desktop browser and send drafts using
              Phone Link.
            </li>
          </ol>
        </section>

        <figure className="space-y-2">
          <img
            src="/help/windows-phone-link.svg"
            alt="Windows Phone Link pairing screen on desktop"
            className="w-full rounded-xl border border-slate-200"
          />
          <figcaption className="text-xs text-slate-500">
            Pair your devices to send drafts from Windows.
          </figcaption>
        </figure>

        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Troubleshooting</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Pairing fails:</strong> Ensure Bluetooth is enabled and both devices are
              on the same Wi-Fi network.
            </li>
            <li>
              <strong>Messages not syncing:</strong> Confirm SMS permissions are granted to the
              Link to Windows app.
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Related</h3>
          <p>
            If you’re importing contacts from your Android phone, follow the{' '}
            <Link to="/app/help/import-android" className="underline">
              Android import guide
            </Link>{' '}
            first.
          </p>
        </section>
      </HelpArticleContent>
    </HelpLayout>
  )
}
